import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  GraduationCap, Presentation, Users, Briefcase, Handshake, Sparkles,
  CheckCircle2, Loader2, Building2, Phone, Mail, ArrowRight, ShieldCheck,
} from 'lucide-react';
import API_BASE from '@/lib/API_BASE';

// Must match ALLOWED_TYPES in backend/college-enquiry.php.
const ENQUIRY_TYPES = [
  { value: 'coaching',   label: 'Campus Coaching Program', icon: GraduationCap, desc: 'A full training track delivered to your students, on campus or online.' },
  { value: 'workshop',   label: 'Hands-on Workshop',       icon: Presentation,  desc: 'Short, practical sessions on AI, web development, data science and more.' },
  { value: 'seminar',    label: 'Seminar / Guest Lecture', icon: Users,         desc: 'Industry talks and awareness sessions from working professionals.' },
  { value: 'placement',  label: 'Placement Training',      icon: Briefcase,     desc: 'Aptitude, interview preparation and soft-skills training for final years.' },
  { value: 'internship', label: 'Internship Program',      icon: Sparkles,      desc: 'Structured, project-based internships with certificates for your students.' },
  { value: 'mou',        label: 'MOU / Partnership',       icon: Handshake,     desc: 'A longer-term tie-up between your institution and XceedEdge.' },
];

const BENEFITS = [
  { title: 'Industry-active trainers',  desc: 'Sessions delivered by people who build software for a living, not full-time lecturers.' },
  { title: 'Built around your calendar', desc: 'We schedule around your semester, exams and existing timetable.' },
  { title: 'Project-based, not slides',  desc: 'Students finish with something they built and can show a recruiter.' },
  { title: 'Certificates for every student', desc: 'Completion certificates your students can attach to their CV and LinkedIn.' },
];

const EMPTY_FORM = {
  college_name: '', contact_person: '', designation: '', email: '', phone: '',
  city: '', enquiry_type: 'coaching', interested_in: '', student_count: '',
  preferred_timeline: '', message: '', website: '', // `website` is the honeypot
};

export default function ForColleges() {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);
  const [errors, setErrors]       = useState([]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      // FormData keeps this consistent with the existing contact form endpoint.
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));

      const res = await axios.post(`${API_BASE}/college-enquiry.php`, fd);

      if (res.data?.success) {
        setDone(true);
        setForm(EMPTY_FORM);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors(res.data?.errors || [res.data?.message || 'Something went wrong. Please try again.']);
      }
    } catch (err) {
      setErrors(
        err.response?.data?.errors ||
        [err.response?.data?.message || 'Could not reach the server. Please try again in a moment.']
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25';
  const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-20">
      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="border-b bg-gradient-to-b from-primary/5 to-transparent pt-28 pb-14">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-semibold text-primary">
            <Building2 className="h-3.5 w-3.5" /> For Colleges &amp; Institutions
          </span>

          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-5xl">
            Bring XceedEdge <span className="text-primary">to your campus</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Coaching programs, hands-on workshops, guest lectures and placement training — designed
            with your department and delivered to your students. Tell us what you need and our campus
            team will call you back within 24 hours.
          </p>

          <a
            href="#enquiry"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition hover:brightness-95"
          >
            Request a call back <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* ── What we offer ──────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 pt-14 sm:px-6">
        <h2 className="font-heading text-2xl font-bold sm:text-3xl">What we can run for your students</h2>
        <p className="mt-2 text-muted-foreground">Pick whatever fits — we tailor the syllabus and duration to your department.</p>

        <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ENQUIRY_TYPES.map(({ value, label, icon: Icon, desc }, i) => (
            <motion.button
              key={value}
              type="button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              onClick={() => {
                setField('enquiry_type', value);
                document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={`rounded-2xl border bg-card p-5 text-left transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg ${
                form.enquiry_type === value ? 'border-primary ring-2 ring-primary/25' : ''
              }`}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-3 font-heading font-bold">{label}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
            </motion.button>
          ))}
        </div>
      </section>

      {/* ── Why partner ────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 pt-14 sm:px-6">
        <div className="rounded-3xl border bg-card p-6 sm:p-9">
          <h2 className="font-heading text-2xl font-bold">Why colleges work with us</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-foreground">{b.title}</p>
                  <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Enquiry form ───────────────────────────────────────── */}
      <section id="enquiry" className="mx-auto max-w-3xl scroll-mt-24 px-4 pt-14 sm:px-6">
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl border border-primary/30 bg-primary/5 p-10 text-center"
          >
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <h2 className="mt-4 font-heading text-2xl font-bold">Enquiry received</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
              Thank you — our campus team has your details and will call you within 24 hours to
              discuss the program for your college.
            </p>
            <button
              onClick={() => setDone(false)}
              className="mt-6 rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:bg-muted"
            >
              Submit another enquiry
            </button>
          </motion.div>
        ) : (
          <div className="rounded-3xl border bg-card p-6 sm:p-9">
            <h2 className="font-heading text-2xl font-bold">Tell us what your college needs</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Fill this in and we'll get back to you within 24 hours. Fields marked * are required.
            </p>

            {errors.length > 0 && (
              <div className="mt-5 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
                <ul className="space-y-1 text-sm text-destructive">
                  {errors.map((err, i) => <li key={i}>• {err}</li>)}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {/* Honeypot — hidden from people, irresistible to bots. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setField('website', e.target.value)}
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
                aria-hidden="true"
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelCls}>College / Institution name *</label>
                  <input
                    required value={form.college_name}
                    onChange={(e) => setField('college_name', e.target.value)}
                    placeholder="e.g. Sri Venkateswara Institute of Technology"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Your name *</label>
                  <input
                    required value={form.contact_person}
                    onChange={(e) => setField('contact_person', e.target.value)}
                    placeholder="Full name" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Designation</label>
                  <input
                    value={form.designation}
                    onChange={(e) => setField('designation', e.target.value)}
                    placeholder="e.g. TPO, HOD, Principal" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Email *</label>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="you@college.edu.in" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Phone *</label>
                  <input
                    type="tel" required value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="10-digit mobile number" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>City</label>
                  <input
                    value={form.city}
                    onChange={(e) => setField('city', e.target.value)}
                    placeholder="e.g. Hyderabad" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>What do you need? *</label>
                  <select
                    required value={form.enquiry_type}
                    onChange={(e) => setField('enquiry_type', e.target.value)}
                    className={inputCls}
                  >
                    {ENQUIRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    <option value="other">Something else</option>
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Topics of interest</label>
                  <input
                    value={form.interested_in}
                    onChange={(e) => setField('interested_in', e.target.value)}
                    placeholder="e.g. AI/ML, Full Stack, Data Science" className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Approx. no. of students</label>
                  <input
                    value={form.student_count}
                    onChange={(e) => setField('student_count', e.target.value)}
                    placeholder="e.g. 120" className={inputCls}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelCls}>Preferred timeline</label>
                  <input
                    value={form.preferred_timeline}
                    onChange={(e) => setField('preferred_timeline', e.target.value)}
                    placeholder="e.g. Next semester, January 2027, as soon as possible"
                    className={inputCls}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelCls}>Tell us more *</label>
                  <textarea
                    required rows={5} value={form.message}
                    onChange={(e) => setField('message', e.target.value)}
                    placeholder="e.g. We would like a 4-week AI/ML workshop for our 3rd year CSE students, around 120 of them, preferably in the January semester."
                    className={inputCls}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-sm transition hover:brightness-95 disabled:opacity-60 sm:w-auto sm:px-10"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {submitting ? 'Sending…' : 'Send enquiry'}
              </button>

              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
                Your details go straight to our campus team and are never shared with anyone else.
              </p>
            </form>
          </div>
        )}

        {/* Direct contact fallback */}
        <div className="mt-8 flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-center sm:gap-6">
          <p className="text-sm text-muted-foreground">Prefer to talk directly?</p>
          <a href="mailto:info@xceededge.com"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <Mail className="h-4 w-4" /> info@xceededge.com
          </a>
          <a href="tel:+919999999999"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <Phone className="h-4 w-4" /> Call our campus team
          </a>
        </div>
      </section>
    </motion.div>
  );
}
